import React, { useEffect, useState } from 'react';
import { whatsappRequest } from './api';
import './whatsapp.css';

/** Connection and preferences only. The agent runs without this screen being open. */
export default function WhatsAppPanel({ user }) {
  const [status, setStatus] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [consent, setConsent] = useState(false);
  const [connection, setConnection] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!user) return undefined;
    const controller = new AbortController();
    whatsappRequest(user, 'status', { signal: controller.signal }).then((value) => {
      setStatus(value);
      setPreferences(value.preferences);
    }).catch((cause) => {
      if (cause.name !== 'AbortError') setError(cause.message);
    });
    return () => controller.abort();
  }, [user]);

  useEffect(() => {
    if (!connection || !user) return undefined;
    const controller = new AbortController();
    let polling = false;
    const timer = setInterval(async () => {
      if (Date.now() > connection.expiresAt) {
        setConnection(null);
        setNotice('Your connection link expired. You can create another one.');
        return;
      }
      if (polling) return;
      polling = true;
      try {
        const value = await whatsappRequest(user, 'status', { signal: controller.signal });
        if (value.connected) {
          setStatus(value);
          setPreferences(value.preferences);
          setConnection(null);
          setNotice('WhatsApp is connected. Your agent is ready.');
        }
      } catch (cause) {
        if (cause.name !== 'AbortError') setError(cause.message);
      } finally {
        polling = false;
      }
    }, 4000);
    return () => { clearInterval(timer); controller.abort(); };
  }, [connection, user]);

  async function perform(action) {
    setBusy(true);
    setError('');
    setNotice('');
    try { await action(); } catch (cause) { setError(cause.message); }
    finally { setBusy(false); }
  }

  const change = (name, value) => setPreferences((current) => ({ ...current, [name]: value }));
  const connect = () => perform(async () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    setConnection(await whatsappRequest(user, 'connect', { method: 'POST', body: { consent, timezone } }));
  });
  const save = (event) => {
    event.preventDefault();
    perform(async () => {
      const value = await whatsappRequest(user, 'preferences', { method: 'PUT', body: preferences });
      setStatus(value);
      setPreferences(value.preferences);
      setNotice('Your WhatsApp preferences are saved.');
    });
  };
  const disconnect = () => perform(async () => {
    await whatsappRequest(user, 'connection', { method: 'DELETE' });
    setStatus((current) => ({ ...current, connected: false, phone: '' }));
    setConnection(null);
    setConsent(false);
    setNotice('WhatsApp is disconnected.');
  });

  if (!user) return null;
  return (
    <section className="js-profile-card js-profile-card--wide wa-panel" aria-labelledby="whatsapp-title">
      <h2 id="whatsapp-title">Your WhatsApp agent</h2>
      <p>Get thoughtful job matches, relevant connections, and reminders. Ask questions by replying to your agent.</p>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {!status && !error && <p role="status">Loading WhatsApp settings…</p>}
      {status && !status.connected && (
        !status.available ? <p>WhatsApp connection is coming soon.</p>
          : !status.eligible ? <p>Available with Pro or an active Scout trial.</p> : (
            <div>
              <label className="wa-check"><input type="checkbox" checked={consent}
                onChange={(event) => setConsent(event.target.checked)} disabled={busy} />
                <span>I agree to receive JOBSEEQR job matches, networking suggestions, learning reminders, and account updates on WhatsApp. I can pause or stop them at any time.</span>
              </label>
              {connection ? (
                <p><a href={connection.url} target="_blank" rel="noreferrer">Open WhatsApp and send the connection message</a>
                  <span className="wa-hint">Then return here. This link expires in 10 minutes.</span></p>
              ) : <button type="button" disabled={!consent || busy} onClick={connect}>Connect WhatsApp</button>}
            </div>
          )
      )}
      {status?.connected && preferences && (
        <form onSubmit={save}>
          <p>Connected to {status.phone}</p>
          {!status.eligible && <p>Recommendations are paused until you have Pro or an active Scout trial.</p>}
          <fieldset disabled={busy}>
            <legend>Messages you want</legend>
            {[
              ['enabled', 'Enable WhatsApp notifications'], ['jobs', 'Job opportunities'],
              ['networking', 'Networking recommendations'], ['learning', 'Learning reminders'],
              ['trial', 'Trial-ending reminder'],
            ].map(([name, label]) => (
              <label className="wa-check" key={name}><input type="checkbox" checked={preferences[name]}
                onChange={(event) => change(name, event.target.checked)} /><span>{label}</span></label>
            ))}
            {!status.learningAvailable && <p className="wa-hint">Learning reminders start when you have Pro and an active learning plan.</p>}
            <div className="wa-grid">
              <label>Days between job recommendations<input type="number" min="1" max="30" required value={preferences.job_days}
                onChange={(event) => change('job_days', Number(event.target.value))} /></label>
              <label>Days between learning reminders<input type="number" min="1" max="30" required value={preferences.learning_days}
                onChange={(event) => change('learning_days', Number(event.target.value))} /></label>
              <label>Timezone<input required value={preferences.timezone} onChange={(event) => change('timezone', event.target.value)} /></label>
              <label>Quiet hours start<select value={preferences.quiet_start} onChange={(event) => change('quiet_start', Number(event.target.value))}>
                {Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{String(hour).padStart(2, '0')}:00</option>)}
              </select></label>
              <label>Quiet hours end<select value={preferences.quiet_end} onChange={(event) => change('quiet_end', Number(event.target.value))}>
                {Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{String(hour).padStart(2, '0')}:00</option>)}
              </select></label>
            </div>
          </fieldset>
          <p className="wa-hint">You can also change the frequency or send STOP directly in WhatsApp.</p>
          <div className="wa-actions"><button type="submit" disabled={busy}>Save preferences</button>
            <button type="button" onClick={disconnect} disabled={busy}>Disconnect WhatsApp</button></div>
        </form>
      )}
    </section>
  );
}
